import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import { UPDATE_FEED_PREFERENCES } from '../../../graphql/resolvers/feed/feedMutations/updateFeedPreferences.gql';
import { MY_FEED_MODE } from '../../../graphql/resolvers/feed/feedQueries/myFeedMode.gql';
import type { UserState } from '../../../interfaces/user.interface';

type Mode = 'CURATED' | 'TRENDING';
const MODES: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: 'CURATED', label: 'Curated' },
  { value: 'TRENDING', label: 'Trending' },
];

export const FeedModeSetting = () => {
  const { user } = useSelector((state: UserState) => state.userStore);
  const { data } = useQuery(MY_FEED_MODE, { fetchPolicy: 'cache-and-network' });
  const [updateFeedPreferences, { loading, error }] = useMutation(
    UPDATE_FEED_PREFERENCES,
  );
  const [selected, setSelected] = useState<Mode | null>(null);
  const [saved, setSaved] = useState(false);

  const current: Mode =
    selected ?? (data?.myFeedMode as Mode | null) ?? 'CURATED';

  const choose = async (feedMode: Mode) => {
    if (feedMode === current || loading) return;
    setSelected(feedMode);
    setSaved(false);
    try {
      await updateFeedPreferences({
        variables: { userId: user.id, preferences: { feedMode } },
      });
      setSaved(true);
    } catch {
      setSelected(null); // revert to the server value; error shown below
    }
  };

  return (
    <section className="FeedMode">
      <h3>Feed mode</h3>
      <p>Choose how your feed leans. You can change this anytime.</p>
      <div className="FeedMode__Options">
        {MODES.map((m) => (
          <ButtonComponent
            key={m.value}
            type="button"
            variant={current === m.value ? 'primary' : 'outline'}
            aria-pressed={current === m.value}
            disabled={loading}
            onClick={() => choose(m.value)}
          >
            {m.label}
          </ButtonComponent>
        ))}
      </div>
      {error ? (
        <p className="FeedMode__Error">Couldn't save. Try again.</p>
      ) : null}
      {saved && !error ? <p className="FeedMode__Saved">Saved</p> : null}
    </section>
  );
};
